// components/ErrorBoundary.jsx
//
// The app had no error boundary anywhere. React's default for an uncaught render
// error is to unmount the WHOLE tree, so a throw deep inside the scheduler's
// edit/create modal took the modal (or the page) off screen with no message —
// which is what "sometimes when I open a New Entry, it will close the window
// without any input from me" looks like from the user's side (bug report #12).
// Nothing was logged either: the report's EventsJson came back empty because a
// React render throw is caught by React itself, so the window 'error' listener in
// utils/bugLog never sees it.
//
// This does three things a bare crash did not:
//   1. records the error + component stack into the bug-report event buffer, so
//      the next report carries the stack instead of arriving empty
//   2. shows a small, honest recovery UI instead of silently vanishing
//   3. contains the damage to `children` — the rest of the page keeps working
//
// `resetKeys` re-mounts the subtree when something meaningful changes (e.g. the
// modal is reopened on a different job), so one bad render doesn't wedge the
// surface until a full page refresh.

import React from 'react';
import { recordEvent } from '../utils/bugLog';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Breadcrumb first — this is the whole point, and it must not be able to
    // throw on the way (a boundary that crashes is worse than none at all).
    recordEvent('react.error', {
      boundary: this.props.label || 'unknown',
      message: error?.message ? `${error.name}: ${error.message}` : String(error),
      stack: error?.stack ? String(error.stack).slice(0, 2000) : null,
      componentStack: info?.componentStack ? String(info.componentStack).slice(0, 2000) : null,
    });
    // Also console.error so it lands in the LOG buffer and in dev tools. The
    // patched console in utils/bugLog captures this, giving the report both halves.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.label || 'unknown'}]`, error);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps) {
    // Re-mount the subtree when the caller says the context changed. Compared by
    // value, not identity, so callers can pass plain ids without memoizing.
    const { resetKeys } = this.props;
    if (!this.state.error || !resetKeys) return;
    const prev = prevProps.resetKeys || [];
    const changed = resetKeys.length !== prev.length || resetKeys.some((k, i) => k !== prev[i]);
    if (changed) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error, () => this.setState({ error: null }));

    return (
      <div style={{
        padding: 16, margin: 8, borderRadius: 8, background: '#FEF2F2',
        border: '1px solid #FECACA', color: '#991B1B', fontSize: 13, maxWidth: 520,
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Something broke here</div>
        <div style={{ color: '#7F1D1D', marginBottom: 10 }}>
          This part of the page hit an error and stopped. The rest of the app is still fine.
          The details were captured — please hit the 🐛 button so we get the stack.
        </div>
        <button
          onClick={() => this.setState({ error: null })}
          style={{
            background: '#991B1B', color: '#fff', border: 'none', borderRadius: 6,
            padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}
